import "./BulkDownloadPage.scss";

import Button from "components/Button/Button";
import KaboodleToaster from "components/common/KaboodleToaster";
import LoadingIcon from "components/Loading/LoadingIcon";
import { format, parseISO } from "date-fns";
import { useParams } from "react-router-dom";
import { useGetBulkDownloadRequestQuery } from "services/documents-api";
import { useSelector } from "utils/redux";
import { DATE_AND_TIME_FORMAT } from "utils/date-utils";

const BulkDownloadPage = () => {
  const accessToken = useSelector((state) => state.main.accessToken);
  const { requestId } = useParams<{ requestId: string }>();

  const {
    data: bulkRequest,
    isLoading,
    error,
  } = useGetBulkDownloadRequestQuery(
    {
      requestId: requestId!,
    },
    {
      skip: !requestId,
    },
  );

  const handleDownload = () => {
    if (!accessToken || !requestId) return;

    const downloadUrl = `/api/v1/bulk-document-download-requests/${requestId}/download/`;

    const downloadPromise = fetch(downloadUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Download failed");
        }
        return response.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "documents.zip";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      });

    KaboodleToaster.promiseLoading(downloadPromise, {
      loadingMessage: "Preparing your download...",
      successMessage: "Download prepared successfully.",
      errorMessage: "Failed to download file. Please try again.",
    });
  };

  if (!accessToken) {
    return (
      <div className="bulk-download-container card">
        <div className="card__header">Bulk Document Download</div>
        <div className="card__content">
          <p>Authentication required. Please log in again.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bulk-download-container card">
        <div className="card__header">Bulk Document Download</div>
        <div className="card__content">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "40px 0",
            }}
          >
            <LoadingIcon />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const errorData = error as { data?: { error?: string }; error?: string };
    const errorMessage =
      errorData?.data?.error ||
      errorData?.error ||
      "Failed to load download information.";

    return (
      <div className="bulk-download-container card">
        <div className="card__header">Bulk Document Download</div>
        <div className="card__content">
          <p>{errorMessage}</p>
        </div>
      </div>
    );
  }

  if (!bulkRequest) {
    return (
      <div className="bulk-download-container card">
        <div className="card__header">Bulk Document Download</div>
        <div className="card__content">
          <p>Download request not found.</p>
        </div>
      </div>
    );
  }

  const isExpired = bulkRequest.expires_at
    ? new Date(bulkRequest.expires_at) < new Date()
    : false;

  const isPending = bulkRequest.status === "pending";
  const isProcessing = bulkRequest.status === "processing";
  const isFailed = bulkRequest.status === "failed";
  const isCompleted = bulkRequest.status === "completed";

  // determine the source of the download
  let source = "";
  if (bulkRequest.file) {
    source = `File #${bulkRequest.file}`;
  } else if (bulkRequest.client) {
    source = `Client #${bulkRequest.client}`;
  } else if (bulkRequest.claim) {
    source = `Claim #${bulkRequest.claim}`;
  }

  return (
    <div className="bulk-download-container card">
      <div className="card__header">Bulk Document Download</div>
      <div className="card__content">
        <div style={{ marginBottom: "20px" }}>
          <div>
            <strong>Source:</strong> {source}
          </div>
          <div>
            <strong>Requested:</strong>{" "}
            {format(parseISO(bulkRequest.created), DATE_AND_TIME_FORMAT)}
          </div>
        </div>

        {isPending && (
          <p>Your download request is queued and will be processed shortly.</p>
        )}

        {isProcessing && (
          <>
            <p>
              Your documents are being prepared. This may take a few minutes.
            </p>
            <p>
              Processed: {bulkRequest.processed_documents} /{" "}
              {bulkRequest.total_documents} documents
            </p>
          </>
        )}

        {isFailed && (
          <p>
            {bulkRequest.error_message ||
              "There was an error processing your download request. Please try again or contact support."}
          </p>
        )}

        {isCompleted && (
          <>
            {isExpired ? (
              <p>
                This download link expired on{" "}
                <strong>
                  {format(
                    parseISO(bulkRequest.expires_at),
                    DATE_AND_TIME_FORMAT,
                  )}
                </strong>
                . Please request a new bulk download.
              </p>
            ) : (
              <p>
                Download available below. This link expires on{" "}
                {format(parseISO(bulkRequest.expires_at), DATE_AND_TIME_FORMAT)}
                .
              </p>
            )}

            <div className="download-box">
              <div className="download-details">
                <div>
                  <span>Total Documents:</span>
                  <span>{bulkRequest.total_documents}</span>
                </div>
                <div>
                  <span>Documents Processed:</span>
                  <span>{bulkRequest.processed_documents}</span>
                </div>
              </div>
              <Button onClick={handleDownload} disabled={isExpired}>
                Download Documents (.zip)
              </Button>
            </div>

            {bulkRequest.processed_documents !==
              bulkRequest.total_documents && (
              <p>
                Note:{" "}
                {bulkRequest.total_documents - bulkRequest.processed_documents}{" "}
                document(s) could not be processed due to safety or access
                restrictions.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BulkDownloadPage;
